<template>
    <div v-if="config.display" :style="{'width': config.width || '100%'}" class="control e-control">
        <label-wrapper :label-settings="config"></label-wrapper>
        <div v-if="asField">
            <display-value :id="config.id" :value="value"></display-value>
        </div>
        <div v-else>
            <div v-if="!optionsReady" class="loading-message">
                Getting data. Please wait...
            </div>
            <div v-else>
                <div v-for="(source, sIndex) in value" :key="sIndex">
                    <div class="edit-source-fields">
                        <div class="type-tag" :class="{'no-tag': !currentSources[sIndex].type}">
                            <div v-if="currentSources[sIndex].type" :class="'type-' + currentSources[sIndex].type">
                                {{currentSources[sIndex].type}}
                            </div>
                            <div v-else :class="'no-type-tag'"></div>
                        </div>
                        <ejs-dropdowntree
                            :allowFiltering="true"
                            :filterType="'Contains'"
                            :id="'sourceField_' + sIndex"
                            v-model="value[sIndex].source"
                            :fields="dropdownFields"
                            :floatLabelType="config.floatLabelType || 'Never'"
                            :treeSettings="{ expandOn: 'Click' }"
                            :placeholder="config.placeholder"
                            :changeOnBlur="false"
                            @change="sourceFieldChange(sIndex, $event)"
                            @focus="this.refreshDatasets()"
                            :ref="config.ref || config.id"
                            :data-test-id="dataTestId"
                            :enabled="!config.disabled"
                            :showClearButton="showClearButton"
                        ></ejs-dropdowntree>
                        <ejs-button v-if="currentSources[sIndex].type === 'sds' || currentSources[sIndex].type === 'uiTag'"
                            v-on:click="openParameters(sIndex, $event)"
                            type="button"
                            :ref="(config.ref || config.id) + '_button'"
                            title="Param"
                            :disabled="config.disableEdit || config.disabled"
                            :data-test-id="dataTestId + '_edit_button'"
                            :cssClass="'e-primary source-action-btn'"
                        >Params</ejs-button>
                        <ejs-button
                            v-on:click="removeSource(sIndex)"
                            type="button"
                            :ref="(config.ref || config.id) + '_button'"
                            title="Remove"
                            :disabled="config.disableEdit || config.disabled"
                            :data-test-id="dataTestId + '_edit_button'"
                            :cssClass="'e-link source-action-btn'"
                        >Remove</ejs-button>
                    </div>
                </div>
                <div>
                    <ejs-button
                        v-on:click="newSource()"
                        type="button"
                        :ref="(config.ref || config.id) + '_button'"
                        title="Remove"
                        :disabled="config.disableEdit || config.disabled"
                        :data-test-id="dataTestId + '_edit_button'"
                        :cssClass="'e-primary'"
                    >Add New Datasource</ejs-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { library } from "@common/library/library";
import { controlMixins } from "./controlMixins.js";
import { configStore } from "@common/configStore";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

let editorSettings = {};

export const dataSourceComponent = {
    editorSettings: editorSettings,
    mixins: [controlMixins],
    data() {
        return {
            selectedValue: -1,
            treeOptions: [],
            reloadDatasetsCompleted: false
            // value comes from controlMixins
        };
    },
    computed: {
        currentSources() {
            let output = [];
            if (Array.isArray(this.value) && this.value.length) {
                this.value.forEach((vItem, index) => {
                    let datasetOption = this.options.find((item) => (vItem.source[0] === item.value));

                    if (!datasetOption) {
                        if (!this.reloadDatasetsCompleted) {
                            this.reloadDatasetsCompleted = true;
                            configStore.getDatasetList(true);
                        }

                        datasetOption = this.options.find((item) => (vItem.source[0] === item.value));
                    }

                    if (datasetOption) output.push({ ...datasetOption, index });
                    else output.push({ ...vItem, index }); // pushes default option
                });
            }

            return output;
        },
        dropdownFields() {
            let fields = this.config.fields;
            let setField = (prop, dVal) => (typeof fields[prop] !== 'undefined' ? fields[prop] : (dVal || prop));
            return {
                dataSource: this.treeOptions,
                text: setField('text'),
                value: setField('value'),
                hasChildren: setField('hasChildren', 'hasChild'),
                parentValue: setField('parentValue', 'pId'),
                selectable: setField('selectable'),
                htmlAttributes: setField('htmlAttributes'),
            };
        },
        showClearButton() {
            return typeof this.config.showClearButton !== 'undefined' ? this.config.showClearButton : true;
        }
    },
    watch: {
        'options': {
            handler: function (newValue) {
                // Merge options to prevent the value from being erased
                if (!Array.isArray(newValue)) return;
                if (!library.isEqualObj(newValue, this.treeOptions)) {
                    newValue.forEach((option, index) => {
                        this.treeOptions[index] = option;
                    });
                }
            },
            immediate: true
        },
        'value': function (newVal) {
            if (typeof newVal === 'string') newVal = [newVal]; // matches eg: "1__1__0__Some Dataset"
            if (Array.isArray(newVal) && typeof newVal[0] === 'string') {
                let formattedValue = [];
                newVal.forEach((val) => {
                    formattedValue.push({
                        source: [val],
                        params: {}
                    });
                });
                this.value = formattedValue;
            }
        }
    },
    methods: {
        newSource() {
            let defaultSource = {
                source: [],
                params: {}
            };
            this.value.push(defaultSource);
        },
        removeSource(sIndex) {
            this.value.splice(sIndex, 1);
        },
        sourceFieldChange(sIndex, event) {
            this.value[sIndex].source = event.value;
            setTimeout(() => {
                this.$parent.$emit('update:value', this.value);
            }, 3000);
            // this.$refs[ref].ej2Instances.value = event.value;
            this.action.call(null, event, 'onchange');
        },
        async refreshDatasets() {
            if (Array.isArray(this.treeOptions)) {
                let datasourceList = await configStore.getDatasourceList();

                if (this.treeOptions.length !== datasourceList.length) {
                    this.treeOptions = library.cloneObj(datasourceList);
                }
            }
        },
        openParameters(sIndex, event) {
            // if (!this.value.source) return library.showToast('Select a data source first.', { timeout: 5 });
            let title = 'Source Parameters';
            let parent = this;
            let selectedSource = parent.value[sIndex];
            let sourceOption = parent.currentSources[sIndex];
            let dataObj = {
                config: parent.config,
                controlConfigs: {},
                value: {
                    source: selectedSource.source,
                    params: {}
                },
                opened: true
            };
            let dialogComponentObj = {
                data() {
                    return dataObj;
                },
                computed: {
                    paramValues() {
                        return this.value.params;
                    }
                },
                components: {
                    control: controlComponent,
                    "ejs-dropdownbutton": defineAsyncComponent(() => import('@common/syncfusion/dropdownButton')),
                    "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
                },
                updated() {
                    let controlKeys = Object.keys(this.controlConfigs);
                    controlKeys.forEach((cKey) => {
                        if (this.controlConfigs[cKey].logic) {
                            library.logicParser.call(this, this.controlConfigs[cKey].logic, this.controlConfigs[cKey]);
                        }
                    });
                }
            };
            dialogComponentObj.template = '<div v-if="opened">Show some params';
            let uniqueID = library.getUniqueID();
            // Set the template HTML
            for (let pKey in sourceOption.params) {
                if (pKey === 'length') continue;
                let param = sourceOption.params[pKey];
                let paramControl = library.cloneObj(param);
                let paramControlName = 'param_' + pKey;
                paramControl.name = paramControlName;
                dataObj.controlConfigs[paramControlName] = paramControl;
                // Set default
                if (selectedSource.params && selectedSource.params[pKey]) {
                    dataObj.value.params[pKey] = selectedSource.params[pKey];
                } else {
                    dataObj.value.params[pKey] = param.default;
                }
                dialogComponentObj.template += '<control name="' + paramControlName + '__' + uniqueID + '" :prop-config="controlConfigs[\'' + paramControlName + '\']" :value="value.params[\'' + pKey + '\']" @update:value="value.params[\'' + pKey + '\'] = $event"></control>';
            }
            dialogComponentObj.template += '</div>';
            dialogComponentObj.name = 'sourceParamsDialog' + uniqueID;
            dialogComponentObj = library.createTemplateComponent(dialogComponentObj.name, dialogComponentObj, this.component);
            let dialogSuccessFn = () => {
                this.value[sIndex].params = dataObj.value.params;
                this.$parent.$emit('update:value', this.value);
                this.action.call(null, event, 'onchange');
                this.$forceUpdate();
            };
            return library.showDialog(dialogComponentObj, dialogSuccessFn, false, { title: title, showAsAlert: true });
        },
    },
    components: {
        "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
        "ejs-dropdownbutton": defineAsyncComponent(() => import('@common/syncfusion/dropdownButton')),
        'ejs-dropdowntree': defineAsyncComponent(() => import('@common/syncfusion/dropdownTree')),
    },
};

export default dataSourceComponent;
</script>

<style lang="css" scoped>
.edit-source-fields {
    display: flex;
    margin-bottom: 5px;
}
.edit-source-fields :deep(.e-input-group.e-control-wrapper) {
    flex-grow: 9;
    margin-bottom: 0;
    margin-right: 2px;
}
.source-action-btn {
    margin-left: 5px;
}
.type-tag > div {
    width: 52px;
    padding: 5px;
    text-align: center;
    margin: 4px 4px 0 0;
    border-radius: 6px;
    background: #e1e1e1;
    color: #3a3a3a;
}
.type-tag.no-tag > div {
    opacity: 0;
}
.type-tag > .type-dataset {
    background: #ffe9b9;
    color: #4f3a05;
}
.type-tag > .type-sds {
    background: #d3ffb9;
    color: #043808;
}
</style>
